/**
 * `/api/schedules` — jobs run on a cron schedule.
 *
 * The job catalogue comes from the server, like the transports: a job this
 * build does not have must not appear in the picker.
 */
import type {
  CreateScheduleRequest,
  DeleteScheduleResponse,
  ListQueryParams,
  ScheduleJobListResponse,
  ScheduleListResponse,
  SchedulePreviewResponse,
  ScheduleResponse,
  ScheduleRunListResponse,
  ScheduleRunResponse,
  UpdateScheduleRequest,
} from '@forsys/shared';
import { apiFetch, query } from './client';

export function listScheduleJobs(): Promise<ScheduleJobListResponse> {
  return apiFetch<ScheduleJobListResponse>('/schedules/jobs');
}

/** When `cron` would fire next, in `timeZone`. A bad expression is a 400 `invalid_cron`. */
export function previewSchedule(cron: string, timeZone: string, count = 3): Promise<SchedulePreviewResponse> {
  return apiFetch<SchedulePreviewResponse>(`/schedules/preview${query({ cron, timeZone, count })}`);
}

export function listSchedules(params: ListQueryParams = {}): Promise<ScheduleListResponse> {
  return apiFetch<ScheduleListResponse>(`/schedules${query({ ...params })}`);
}

export function getSchedule(id: number): Promise<ScheduleResponse> {
  return apiFetch<ScheduleResponse>(`/schedules/${id}`);
}

export function createSchedule(body: CreateScheduleRequest): Promise<ScheduleResponse> {
  return apiFetch<ScheduleResponse>('/schedules', { method: 'POST', json: body });
}

/** Omitted fields are left alone. */
export function updateSchedule(id: number, body: UpdateScheduleRequest): Promise<ScheduleResponse> {
  return apiFetch<ScheduleResponse>(`/schedules/${id}`, { method: 'PATCH', json: body });
}

/** Takes its run history with it. */
export function deleteSchedule(id: number): Promise<DeleteScheduleResponse> {
  return apiFetch<DeleteScheduleResponse>(`/schedules/${id}`, { method: 'DELETE' });
}

/** Runs it now and answers when the run has finished. A failed job is a `failed` run, not an error. */
export function runSchedule(id: number): Promise<ScheduleRunResponse> {
  return apiFetch<ScheduleRunResponse>(`/schedules/${id}/run`, { method: 'POST' });
}

export function listScheduleRuns(id: number, params: ListQueryParams = {}): Promise<ScheduleRunListResponse> {
  return apiFetch<ScheduleRunListResponse>(`/schedules/${id}/runs${query({ ...params })}`);
}
